import { useMemo } from 'react';
import { clsx } from 'clsx';
import type { FlowGraph, FlowNode } from '../../utils/registryView';
import styles from './AgentFlowMap.module.css';

const COL_W = 184;
const COL_GAP = 56;
const MARGIN_X = 16;
const TOP = 48;
const BOX_H = 32;
const BOX_GAP = 10;
const MAX_LABEL = 26;

interface Placed {
  node: FlowNode;
  x: number;
  y: number;
  col: number;
}

function truncate(label: string): string {
  return label.length > MAX_LABEL ? `${label.slice(0, MAX_LABEL - 1)}…` : label;
}

/**
 * One role's pipeline as an SVG, generated from the graph the registry helpers derive from the
 * manifests. Skill nodes are buttons (click or Enter opens the skill); trigger nodes are static. Every
 * skill ends in the shared Tasks inbox, drawn once as the band at the bottom.
 */
export function AgentFlowMap({
  graph,
  onSelectSkill,
}: {
  graph: FlowGraph;
  onSelectSkill?: (skillKey: string) => void;
}) {
  const layout = useMemo(() => {
    const placed = new Map<string, Placed>();
    graph.columns.forEach((column, col) => {
      column.nodes.forEach((node, row) => {
        placed.set(node.id, {
          node,
          x: MARGIN_X + col * (COL_W + COL_GAP),
          y: TOP + row * (BOX_H + BOX_GAP),
          col,
        });
      });
    });
    const n = graph.columns.length;
    const maxRows = Math.max(1, ...graph.columns.map((c) => c.nodes.length));
    const stackBottom = TOP + maxRows * (BOX_H + BOX_GAP);
    const inboxY = stackBottom + 22;
    const width = MARGIN_X * 2 + n * COL_W + Math.max(0, n - 1) * COL_GAP;
    const height = inboxY + 64;
    const firstSkillCol = graph.columns.findIndex((c) => c.tone !== 'trigger');
    const inboxX = MARGIN_X + Math.max(0, firstSkillCol) * (COL_W + COL_GAP);
    const inboxW = width - MARGIN_X - inboxX;
    const lastCol = graph.columns[n - 1];
    const lastCenterX = MARGIN_X + (n - 1) * (COL_W + COL_GAP) + COL_W / 2;
    const lastBottomY = lastCol ? TOP + lastCol.nodes.length * (BOX_H + BOX_GAP) : TOP;
    return { placed, width, height, inboxX, inboxW, inboxY, lastCenterX, lastBottomY, n };
  }, [graph]);

  if (graph.columns.length === 0) return null;

  return (
    <div className={styles.canvas}>
      <svg
        className={styles.svg}
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        width={layout.width}
        height={layout.height}
        role="img"
        aria-label="Agent flow map"
      >
        {graph.columns.map((column, col) => (
          <text
            key={column.id}
            className={styles.columnTitle}
            x={MARGIN_X + col * (COL_W + COL_GAP)}
            y={TOP - 16}
          >
            {column.title}
          </text>
        ))}

        {graph.edges.map((edge) => {
          const from = layout.placed.get(edge.from);
          const to = layout.placed.get(edge.to);
          if (!from || !to) return null;
          const forward = to.col > from.col;
          const x1 = forward ? from.x + COL_W : from.x;
          const x2 = forward ? to.x : to.x + COL_W;
          const y1 = from.y + BOX_H / 2;
          const y2 = to.y + BOX_H / 2;
          const bend = forward ? COL_GAP / 2 : -COL_GAP / 2;
          const head = forward
            ? `${x2 - 6},${y2 - 4} ${x2},${y2} ${x2 - 6},${y2 + 4}`
            : `${x2 + 6},${y2 - 4} ${x2},${y2} ${x2 + 6},${y2 + 4}`;
          return (
            <g key={`${edge.from}→${edge.to}`}>
              <path
                className={styles.edge}
                d={`M${x1} ${y1} C ${x1 + bend} ${y1}, ${x2 - bend} ${y2}, ${x2} ${y2}`}
              >
                <title>{`${edge.from} → ${edge.to}: ${edge.reason}`}</title>
              </path>
              <polygon className={styles.edgeHead} points={head} />
            </g>
          );
        })}

        {[...layout.placed.values()].map(({ node, x, y }) => {
          const tone = graph.columns.find((c) => c.id === node.column)?.tone ?? 'propose';
          const clickable = !node.trigger && !!onSelectSkill;
          const select = () => {
            if (clickable) onSelectSkill?.(node.id);
          };
          return (
            <g
              key={node.id}
              className={styles.nodeGroup}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              aria-label={clickable ? `Open ${node.label}` : undefined}
              onClick={select}
              onKeyDown={(event) => {
                if (clickable && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  select();
                }
              }}
            >
              <rect
                className={clsx(
                  styles.node,
                  styles[`tone_${tone}`],
                  node.dormant && styles.dormant,
                  !clickable && styles.nodeStatic,
                )}
                x={x}
                y={y}
                width={COL_W}
                height={BOX_H}
                rx={7}
              >
                <title>{node.dormant ? `${node.label} (dormant)` : node.label}</title>
              </rect>
              <text
                className={clsx(styles.nodeText, node.dormant && styles.nodeTextDormant)}
                x={x + 11}
                y={y + 20}
              >
                {truncate(node.label)}
              </text>
              {node.checkpoints && (
                <circle className={styles.checkpointDot} cx={x + COL_W - 11} cy={y + BOX_H / 2} r={3.5}>
                  <title>Can hold on the always-ask floor</title>
                </circle>
              )}
              {node.waitingOn.length > 0 && (
                <circle
                  className={styles.waitingRing}
                  cx={x + COL_W - (node.checkpoints ? 24 : 11)}
                  cy={y + BOX_H / 2}
                  r={3.5}
                >
                  <title>{`Waiting on a connection: ${node.waitingOn.join(', ')}`}</title>
                </circle>
              )}
            </g>
          );
        })}

        <path
          className={styles.inboxLink}
          d={`M${layout.lastCenterX} ${layout.lastBottomY} L ${layout.lastCenterX} ${layout.inboxY}`}
        />
        <rect
          className={styles.inbox}
          x={layout.inboxX}
          y={layout.inboxY}
          width={layout.inboxW}
          height={44}
          rx={10}
        />
        <text className={styles.inboxTitle} x={layout.inboxX + 16} y={layout.inboxY + 19}>
          Shared Tasks inbox
        </text>
        <text className={styles.inboxSub} x={layout.inboxX + 16} y={layout.inboxY + 34}>
          Needs attention · Agents&apos; work · History — one channel for every agent
        </text>
      </svg>

      <div className={styles.legend}>
        {graph.columns.map((column) => (
          <span key={column.id} className={styles.legendItem}>
            <span className={clsx(styles.swatch, styles[`swatch_${column.tone}`])} />
            {column.title}
          </span>
        ))}
        <span className={styles.legendItem}>
          <span className={clsx(styles.swatch, styles.swatchDormant)} />
          Dormant (defined, not reachable)
        </span>
        <span className={styles.legendItem}>
          <span className={styles.swatchDot} />
          Can hold for approval (checkpoints)
        </span>
        {graph.columns.some((c) => c.nodes.some((n) => n.waitingOn.length > 0)) && (
          <span className={styles.legendItem}>
            <span className={styles.swatchRing} />
            Waiting on a data connection (runs, records the gap)
          </span>
        )}
      </div>
    </div>
  );
}
